import { AxiosResponse } from 'axios';
import useUserData from 'hooks/useUserData';
import { QueryFunctionContext, QueryKey, useQuery } from 'react-query';
import { insightsQueryKeys } from './query-keys';
import { useMemo } from 'react';
import { InsightsAPI, PythonInsightsAPI } from '..';
import { InsightFilterType } from '../apis/PythonInsightsAPI';
import _ from 'lodash';
import { InsightsResponseType } from './useInsightsQuery';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { InsightSummaryPayload } from '../apis/InsightsAPI';

export enum InsightSummaryStatusType {
  PUBLISHED = 'PUBLISHED',
  REVIEW_PENDING = 'REVIEW_PENDING',
}

export type InsightSummaryType = {
  allEndorsedSkills: any[];
  articleInfo?: ArticleType;
  autoDraftFlag: boolean;
  companyId: string;
  createdAt: string | Date;
  createdBy: string;
  description: string;
  division: string;
  hasAttachments: boolean;
  hasEmbeddedImages: boolean;
  id: string;
  isBookMarked: boolean;
  isDraft: boolean;
  isFlagged: boolean;
  projectId: string;
  publishedAt: string | Date;
  refArticleId: string;
  sample: boolean;
  sourceObjectType: string;
  specCategory: string;
  specSection: string;
  status: InsightSummaryStatusType;
  title: string;
  totalComments: number;
  totalLikes: number;
  totalSocialActivities: number;
  totalViews: number;
  uniformatL1: string;
  uniformatL2: string;
  uniformatL3: string;
  userLiked: boolean;
  userViewed: boolean;
};

export type GetInsightsSummaryQueryFnType = (
  options: InsightSummaryPayload & {
    context: QueryFunctionContext;
    novaSearchKeyWord?: string;
  },
) => Promise<InsightsResponseType> | undefined;

export type GetInsightsSummaryQueryKeyType = (options: {
  directory: string[];
  companyId: string;
  filters?: InsightFilterType[];
  groupBy: string;
  novaSearchKeyWord?: string;
  includeBWInsights: boolean;
}) => QueryKey;

function useInsightsSummaryQuery({
  directory,
  filters,
  groupBy,
  includeBWInsights,
  novaSearchKeyWord = '',
  projectId,
  shouldFetch = true,
  getInsightsSummaryQueryFn,
  getInsightsSummaryQueryKey,
}: {
  directory: string[];
  filters?: InsightFilterType[];
  groupBy: string;
  includeBWInsights: boolean;
  novaSearchKeyWord?: string;
  projectId?: string;
  shouldFetch?: boolean;
  getInsightsSummaryQueryFn?: GetInsightsSummaryQueryFnType;
  getInsightsSummaryQueryKey?: GetInsightsSummaryQueryKeyType;
}) {
  const { companyId } = useUserData();
  const { data: insightsSummaryData, ...rest } = useQuery<
    AxiosResponse<
      {
        insightGroupByList: {
          groupByFieldName: string;
          groupByFieldValue: string | string[];
          insights: InsightSummaryType[];
          insightsCount: string;
        }[];
      },
      Error
    >
  >({
    queryKey:
      getInsightsSummaryQueryKey?.({
        companyId,
        directory,
        filters,
        groupBy,
        novaSearchKeyWord,
        includeBWInsights,
      }) ||
      (novaSearchKeyWord
        ? insightsQueryKeys.insightsSummarySearch({
            companyId,
            groupBy,
            directory,
            novaSearchKeyWord,
            filters,
            includeBWInsights,
          })
        : insightsQueryKeys.insightsSummary({
            companyId,
            groupBy,
            directory,
            filters,
            includeBWInsights,
          })),
    queryFn: (context) => {
      return (
        getInsightsSummaryQueryFn?.({
          companyId,
          context,
          novaSearchKeyWord,
          groupBy,
          includeBWInsights,
        }) ||
        (novaSearchKeyWord
          ? PythonInsightsAPI.getInsightsSummarySearch({
              companyId,
              novaSearchKeyWord,
              filters,
              groupBy,
              includeBWInsights,
            })
          : InsightsAPI.getInsightsSummary({
              companyId,
              filters,
              groupBy,
              projectId,
              includeBWInsights,
            }))
      );
    },
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    enabled: !!(companyId && directory) && shouldFetch,
  });

  const insightsSummary = useMemo(
    () => insightsSummaryData?.data?.insightGroupByList?.[0]?.insights || [],
    [insightsSummaryData],
  );

  return {
    insightsSummary,
    ...rest,
  };
}

export default useInsightsSummaryQuery;
